<script setup lang="ts">
import PawRegularIcon from '@/assets/icons/paw-regular.svg?component'
import PawSolidIcon from '@/assets/icons/paw-solid.svg?component'
import TruckRegularIcon from '@/assets/icons/truck-regular.svg?component'
import TruckSolidIcon from '@/assets/icons/truck-solid.svg?component'
import UserRegular from '@/assets/icons/user-regular.svg?component'
import UserSolid from '@/assets/icons/user-solid.svg?component'
import LykaLogo from '@/assets/lyka-logo.svg?component'
import { useFeatureFlags } from '@/composables/useFeatureFlags'
import { GiftIcon } from '@heroicons/vue/24/outline'
import { LIcon } from '@repo/lui'
import { type Component, computed } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'

type NavItem = {
  label: string
  name: string
  path: string
  icon?: Component
  activeIcon?: Component
  heroIcon?: Component
}

const route = useRoute()
const router = useRouter()
const meta = computed(() => router.currentRoute.value.meta)

const flags = useFeatureFlags()
// @TODO (R2-4066) remove this feature flag when RAF is done
const isReferAFriendEnabled = computed(() => flags.hasFeature('feature/refer_a_friend'))

const items = computed<NavItem[]>(() => {
  const nav: NavItem[] = [
    { label: 'Deliveries', name: 'deliveries', path: '/deliveries', icon: TruckRegularIcon, activeIcon: TruckSolidIcon },
    { label: 'Dogs', name: 'dogs', path: '/dogs', icon: PawRegularIcon, activeIcon: PawSolidIcon },
    ...(isReferAFriendEnabled.value?[{
      label: 'Get 50% off',
      name: 'refer-a-friend',
      path: '/account/refer-a-friend',
      heroIcon: GiftIcon,
    }]:[]),
    { label: 'Account', name: 'account-home', path: '/account', icon: UserRegular, activeIcon: UserSolid }
  ]

  return nav
})
</script>

<!-- TODO (Matt): #fcfbf9 should be a token called surface-subtle which doesn't exist in lyka-common -->
<!-- TODO (Matt): surface-inactive should also be replicated as border-muted token -->
<template>
  <header v-if="!meta.fullscreen" class="sticky bottom-0 z-30 md:top-0 md:bottom-auto">
    <RouterLink aria-label="Lyka" class="sr-only md:hidden" to="/">
      <LykaLogo />
    </RouterLink>

    <nav
      aria-label="Navigation"
      class="border-surface-inactive flex border bg-[#fcfbf9] px-5 pb-1.5 text-xs sm:px-6 md:hidden"
    >
      <h2 class="sr-only">Navigation</h2>
      <ul class="container mx-auto -mt-px flex grow">
        <li v-for="item in items" :key="item.name" class="flex flex-1">
          <RouterLink
            class="text-content-neutral hover:text-content-secondary focus-visible:text-content-secondary aria-[current=page]:text-content-primary flex w-full flex-col items-center gap-2 border-t-3 border-transparent border-t-transparent py-3 aria-[current=page]:border-t-current"
            :to="item.path"
          >
            <LIcon v-if="item.heroIcon" :is="item.heroIcon" size="md" aria-hidden="true" />
            <component
              v-else
              :is="route.name === item.name ? item.activeIcon : item.icon"
              aria-hidden="true"
              class="h-6"
              role="img"
            />
            {{ item.label }}
          </RouterLink>
        </li>
      </ul>
    </nav>

    <div
      class="bg-surface-brand hidden shadow-[2px_6px_20px_0_rgba(0,0,0,0.08),2px_2px_4px_-4px_rgba(0,0,0,0.03)] md:block"
    >
      <div class="mx-auto flex max-w-7xl items-center justify-between px-8 py-2 lg:px-10">
        <RouterLink aria-label="Lyka" to="/">
          <LykaLogo class="text-content-invert h-9" />
        </RouterLink>

        <nav aria-label="Navigation" class="flex">
          <h2 class="sr-only">Navigation</h2>
          <ul class="flex text-sm">
            <li v-for="item in items" :key="item.name" class="flex">
              <RouterLink
                class="text-content-brand-invert hover:bg-btn-primary-hover focus-visible:bg-btn-primary-hover flex h-8 items-center rounded-full px-3"
                active-class="bg-surface-dark"
                :to="item.path"
              >
                {{ item.label }}
              </RouterLink>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </header>
</template>
